import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import './i18n';
import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer/Footer';
import LanguageSwitch from './components/LanguageSwitch/LanguageSwitch';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import Loading from './components/Loading/Loading';
import './App.css';

// Public pages are bundled with the app (about 90 KB minified in all): a lazy chunk made the
// whole screen, navbar included, flash to the loading fallback on every page change and replaced
// the prerendered page with it on first load. Only the admin is split into its own chunks.
import Home from './pages/Home/Home';
import About from './pages/About/About';
import Solutions from './pages/Solutions/Solutions';
import Portfolio from './pages/Portfolio/Portfolio';
import Resources from './pages/Resources/Resources';
import Contact from './pages/Contact/Contact';
import LegalNotice from './pages/Legal/LegalNotice';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import Careers from './pages/Careers/Careers';
import JobDetail from './pages/Careers/JobDetail';
import NotFound from './pages/NotFound/NotFound';

const AdminLogin = lazy(() => import('./pages/Admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const AdminExpertise = lazy(() => import('./pages/Admin/AdminExpertise'));
const AdminClients = lazy(() => import('./pages/Admin/AdminClients'));
const AdminTimeline = lazy(() => import('./pages/Admin/AdminTimeline'));
const AdminAchievements = lazy(() => import('./pages/Admin/AdminAchievements'));
const AdminSolutions = lazy(() => import('./pages/Admin/AdminSolutions'));
const AdminPortfolio = lazy(() => import('./pages/Admin/AdminPortfolio'));
const AdminResources = lazy(() => import('./pages/Admin/AdminResources'));
const AdminTeam = lazy(() => import('./pages/Admin/AdminTeam'));
const AdminEnquiries = lazy(() => import('./pages/Admin/AdminEnquiries'));
const AdminCareers = lazy(() => import('./pages/Admin/AdminCareers'));

// Component to scroll to top on route change
function ScrollToTopOnNavigate() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Layout component that conditionally renders Navbar/Footer
function Layout({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <>
      <ScrollToTopOnNavigate />
      {!isAdminRoute && <Navbar />}
      {!isAdminRoute && <LanguageSwitch />}
      {children}
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <ScrollToTop />}
    </>
  );
}

function App() {
  const { i18n, ready } = useTranslation();

  if (!ready) {
    return <Loading />;
  }

  return (
    <HelmetProvider>
      <Router>
        <div className="App" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
          <Suspense fallback={<Loading />}>
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/solutions" element={<Solutions />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/careers/:jobId" element={<JobDetail />} />
                <Route path="/legal-notice" element={<LegalNotice />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/admin" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/expertise" element={<AdminExpertise />} />
                <Route path="/admin/clients" element={<AdminClients />} />
                <Route path="/admin/timeline" element={<AdminTimeline />} />
                <Route path="/admin/achievements" element={<AdminAchievements />} />
                <Route path="/admin/solutions" element={<AdminSolutions />} />
                <Route path="/admin/portfolio" element={<AdminPortfolio />} />
                <Route path="/admin/resources" element={<AdminResources />} />
                <Route path="/admin/team" element={<AdminTeam />} />
                <Route path="/admin/enquiries" element={<AdminEnquiries />} />
                <Route path="/admin/careers" element={<AdminCareers />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Layout>
          </Suspense>
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;